import { useEffect, useRef, useCallback } from 'react';
import { SupportTicketSummaryDto } from '../types';

const POLLING_INTERVAL = 10_000; // 10 seconds

interface UseTicketListPollingOptions {
  /** Current tickets list */
  tickets: SupportTicketSummaryDto[];
  /** Function to fetch fresh tickets (first page only) */
  fetchTickets: () => Promise<SupportTicketSummaryDto[] | null>;
  /** Called when ticket list has changed */
  onUpdate: (tickets: SupportTicketSummaryDto[]) => void;
  /** Whether polling is enabled */
  enabled?: boolean;
}

/**
 * Polls for changes in the support ticket list (new tickets, status changes, read status).
 * Only updates state when changes are detected.
 */
export function useTicketListPolling({
  tickets,
  fetchTickets,
  onUpdate,
  enabled = true,
}: UseTicketListPollingOptions) {
  const ticketsRef = useRef(tickets);
  ticketsRef.current = tickets;

  const poll = useCallback(async () => {
    if (ticketsRef.current.length === 0) return;

    try {
      const fresh = await fetchTickets();
      if (!fresh) return;

      const current = ticketsRef.current;

      // Check for any differences
      if (fresh.length !== current.length) {
        onUpdate(fresh);
        return;
      }

      const hasChanges = fresh.some((freshTicket, i) => {
        const currentTicket = current[i];
        if (!currentTicket) return true;
        return (
          freshTicket.id !== currentTicket.id ||
          freshTicket.status !== currentTicket.status ||
          freshTicket.isReadByAdmin !== currentTicket.isReadByAdmin ||
          freshTicket.isReadByUser !== currentTicket.isReadByUser ||
          freshTicket.messageCount !== currentTicket.messageCount ||
          freshTicket.updatedAt !== currentTicket.updatedAt
        );
      });

      if (hasChanges) {
        onUpdate(fresh);
      }
    } catch {
      // Silently ignore polling errors
    }
  }, [fetchTickets, onUpdate]);

  useEffect(() => {
    if (!enabled || tickets.length === 0) return;

    const intervalId = setInterval(poll, POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, [enabled, tickets.length, poll]);
}
